import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { eventLocalTime, eventLocalDate } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, MapPin, FileText, Clock, Calendar as CalendarIcon } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const TYPE_META = {
  appointment: { label: "Appointment", color: "#60a5fa" },
  follow_up: { label: "Follow-up", color: "#f5a623" },
  call: { label: "Call", color: "#C5E26A" },
  other: { label: "Other", color: "#888888" },
};

function pad(n) { return n.toString().padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function monthLabel(d) { return d.toLocaleDateString(undefined, { month: "long", year: "numeric" }); }

export default function Calendar() {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // either an event obj or a "new:YYYY-MM-DD" string

  // Calculate the grid: start on Sunday of week containing the first of month
  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const startWeekday = first.getDay(); // 0=Sun
    const cells = [];
    // Leading blanks
    for (let i = 0; i < startWeekday; i++) {
      const d = new Date(first);
      d.setDate(d.getDate() - (startWeekday - i));
      cells.push({ date: d, inMonth: false });
    }
    for (let dayNum = 1; dayNum <= last.getDate(); dayNum++) {
      cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), dayNum), inMonth: true });
    }
    // Trailing to fill 6 rows (42 cells)
    while (cells.length < 42) {
      const lastCell = cells[cells.length - 1].date;
      const d = new Date(lastCell);
      d.setDate(d.getDate() + 1);
      cells.push({ date: d, inMonth: false });
    }
    return cells;
  }, [cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const first = cells[0].date;
      const lastCell = cells[cells.length - 1].date;
      const { data } = await api.get("/events", {
        params: { start: ymd(first), end: ymd(lastCell) },
      });
      setEvents(data || []);
    } catch (e) {
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  }, [cells]);

  // eslint-disable-next-line
  useEffect(() => { load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const ev of events) {
      const key = eventLocalDate(ev);
      (m[key] = m[key] || []).push(ev);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => (eventLocalTime(a) || "00:00").localeCompare(eventLocalTime(b) || "00:00")));
    return m;
  }, [events]);

  const onDayClick = (d) => {
    setSelectedDate(d);
  };

  const onCreateForDate = (d) => {
    setEditing({ _newDate: ymd(d) });
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="calendar-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Schedule</div>
          <h1 className="h-display text-3xl sm:text-4xl">Calendar</h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>
            Your follow ups, appointments, and calls. All in one view.
          </p>
        </div>
        <button
          onClick={() => onCreateForDate(today)}
          className="btn-primary text-sm flex items-center gap-2 shrink-0"
          data-testid="add-event-button"
        >
          <Plus size={14} /> Add event
        </button>
      </div>

      <div className="surface-card p-3 sm:p-5">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCursor((c) => addMonths(c, -1))}
            className="p-2 rounded-xl transition hover:bg-white/5"
            data-testid="cal-prev-month"
            title="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <h2 className="h-display text-base sm:text-xl">{monthLabel(cursor)}</h2>
            <button
              onClick={() => setCursor(startOfMonth(new Date()))}
              className="btn-ghost text-xs"
              data-testid="cal-today-button"
            >
              Today
            </button>
          </div>
          <button
            onClick={() => setCursor((c) => addMonths(c, 1))}
            className="p-2 rounded-xl transition hover:bg-white/5"
            data-testid="cal-next-month"
            title="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 mb-1 sm:mb-2">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={i} className="font-mono text-[10px] uppercase tracking-wider text-center py-1 sm:py-2" style={{ color: "var(--text-muted)" }}>
              <span className="sm:hidden">{d}</span>
              <span className="hidden sm:inline">{["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][i]}</span>
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {cells.map(({ date, inMonth }, i) => {
            const key = ymd(date);
            const dayEvents = eventsByDay[key] || [];
            const isToday = sameDay(date, today);
            const isSelected = selectedDate && sameDay(date, selectedDate);
            return (
              <button
                key={i}
                onClick={() => onDayClick(date)}
                data-testid={`cal-day-${key}`}
                className="text-left p-1.5 sm:p-2 transition min-h-[58px] sm:min-h-[88px] flex flex-col"
                style={{
                  background: isSelected ? "rgba(255,255,255,0.06)" : "var(--surface2)",
                  border: isToday ? "1px solid var(--text)" : "1px solid var(--border)",
                  opacity: inMonth ? 1 : 0.4,
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="font-mono text-xs font-semibold"
                    style={{ color: "var(--text)" }}
                  >
                    {date.getDate()}
                  </span>
                  {dayEvents.length > 0 && (
                    <span className="hidden sm:inline-block font-mono text-[9px] px-1 rounded" style={{ background: "rgba(255,255,255,0.08)", color: "var(--text)" }}>
                      {dayEvents.length}
                    </span>
                  )}
                </div>
                {/* Mobile: dot indicators only */}
                <div className="flex sm:hidden gap-0.5 flex-wrap mt-auto">
                  {dayEvents.slice(0, 3).map((ev) => (
                    <span
                      key={ev.id}
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: TYPE_META[ev.event_type]?.color || "#888" }}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <span className="text-[8px]" style={{ color: "var(--text-muted)" }}>+</span>
                  )}
                </div>
                {/* Desktop: event title chips */}
                <div className="hidden sm:block space-y-0.5 flex-1 overflow-hidden">
                  {dayEvents.slice(0, 2).map((ev) => (
                    <div
                      key={ev.id}
                      className="text-[10px] truncate px-1.5 py-0.5 rounded"
                      style={{
                        background: `${TYPE_META[ev.event_type]?.color || "#888"}22`,
                        color: TYPE_META[ev.event_type]?.color || "#888",
                      }}
                      title={ev.title}
                    >
                      {eventLocalTime(ev) ? `${eventLocalTime(ev)} ` : ""}{ev.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-[9px]" style={{ color: "var(--text-muted)" }}>+ {dayEvents.length - 2} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
          {Object.entries(TYPE_META).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: v.color }} />
              {v.label}
            </div>
          ))}
        </div>

        {loading && <div className="text-center text-xs mt-3" style={{ color: "var(--text-muted)" }}>Loading…</div>}
      </div>

      {/* Selected day panel */}
      {selectedDate && (
        <DayPanel
          date={selectedDate}
          events={eventsByDay[ymd(selectedDate)] || []}
          onClose={() => setSelectedDate(null)}
          onAdd={() => onCreateForDate(selectedDate)}
          onEdit={(ev) => setEditing(ev)}
          onDelete={(ev) => {
            if (ev.source !== "manual") {
              toast.info("Auto-imported events can't be deleted here", { description: "Edit the source (e.g. lead follow-up date) instead." });
              return;
            }
            setPendingDelete(ev);
          }}
        />
      )}

      {editing && (
        <EventModal
          event={editing._newDate ? null : editing}
          defaultDate={editing._newDate || (editing && editing.date)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this event?"
        description={pendingDelete ? `"${pendingDelete.title}" will be removed from your calendar. This can't be undone.` : ""}
        confirmLabel="Delete event"
        destructive
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          const ev = pendingDelete;
          setPendingDelete(null);
          if (!ev) return;
          try {
            await api.delete(`/events/${ev.id}`);
            toast.success("Event deleted");
            load();
          } catch (e) {
            toast.error("Could not delete event");
          }
        }}
      />
    </div>
  );
}

function DayPanel({ date, events, onClose, onAdd, onEdit, onDelete }) {
  const label = date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="surface-card p-5 mt-4" data-testid="cal-day-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="h-display text-lg">{label}</h3>
        <div className="flex items-center gap-2">
          <button onClick={onAdd} className="btn-primary text-xs flex items-center gap-2" data-testid="cal-add-event-for-day">
            <Plus size={12} /> Add
          </button>
          <button onClick={onClose} className="p-1.5 rounded transition hover:bg-white/5"><X size={16} /></button>
        </div>
      </div>
      {events.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Nothing scheduled. Click <strong>Add</strong> to drop something in.</p>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="p-3 rounded-2xl flex items-start gap-3"
              style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
              data-testid={`cal-event-${ev.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-sm truncate">{ev.title}</div>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: `${TYPE_META[ev.event_type]?.color || "#888"}22`, color: TYPE_META[ev.event_type]?.color }}>
                    {TYPE_META[ev.event_type]?.label}
                  </span>
                  {ev.source === "lead" && (
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: "rgba(96,165,250,0.12)", color: "#60a5fa" }}>
                      From Lead
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 flex-wrap text-xs" style={{ color: "var(--text-muted)" }}>
                  {eventLocalTime(ev) && <span className="flex items-center gap-1"><Clock size={11} /> {eventLocalTime(ev)}{ev.duration_minutes ? ` · ${ev.duration_minutes}m` : ""}</span>}
                  {ev.location && <span className="flex items-center gap-1"><MapPin size={11} /> {ev.location}</span>}
                </div>
                {ev.notes && (
                  <div className="text-xs mt-2 flex items-start gap-1.5" style={{ color: "var(--text-muted)" }}>
                    <FileText size={11} className="mt-0.5 shrink-0" />
                    <span>{ev.notes}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {ev.source === "manual" && (
                  <>
                    <button onClick={() => onEdit(ev)} className="btn-ghost text-xs" data-testid={`cal-edit-${ev.id}`}>Edit</button>
                    <button onClick={() => onDelete(ev)} className="p-1.5 rounded transition hover:bg-red-500/10" style={{ color: "var(--red)" }} title="Delete">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventModal({ event, defaultDate, onClose, onSaved }) {
  const [title, setTitle] = useState(event?.title || "");
  const [date, setDate] = useState(event?.date || defaultDate || ymd(new Date()));
  const [time, setTime] = useState(event?.time || "");
  const [duration, setDuration] = useState(event?.duration_minutes || "");
  const [eventType, setEventType] = useState(event?.event_type || "appointment");
  const [location, setLocation] = useState(event?.location || "");
  const [notes, setNotes] = useState(event?.notes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        date,
        time: time || null,
        duration_minutes: duration ? Number(duration) : null,
        event_type: eventType,
        location: location || null,
        notes: notes || null,
      };
      if (event?.id) {
        await api.patch(`/events/${event.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      onSaved();
    } catch (err) {
      toast.error("Could not save event", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="event-modal"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl flex flex-col"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          maxHeight: "92vh",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 sticky top-0 z-10" style={{ background: "var(--surface)" }}>
          <div className="flex items-center gap-2">
            <CalendarIcon size={16} style={{ color: "var(--text)" }} />
            <h2 className="font-mono text-sm uppercase tracking-wider" style={{ color: "var(--text)" }}>
              {event ? "Edit event" : "New event"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 transition hover:opacity-70"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="field-label">Title <span className="req">*</span></label>
            <input
              className="input-base"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Call John about 123 Main St"
              autoFocus
              data-testid="event-title-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className="field-label">Date</label>
              <input
                type="date"
                className="input-base"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="field-label">Time</label>
              <input
                type="time"
                className="input-base"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="event-time-input"
              />
            </div>
            <div>
              <label className="field-label">Duration (min)</label>
              <input
                type="number"
                min="0"
                className="input-base"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="30"
                data-testid="event-duration-input"
              />
            </div>
          </div>
          <div>
            <label className="field-label">Type</label>
            <select
              className="input-base"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              data-testid="event-type-select"
            >
              <option value="appointment">Appointment</option>
              <option value="call">Call</option>
              <option value="follow_up">Follow-up</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="field-label">Location (optional)</label>
            <input
              className="input-base"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Address, Zoom link, etc."
              data-testid="event-location-input"
            />
          </div>
          <div>
            <label className="field-label">Notes (optional)</label>
            <textarea
              className="input-base"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything you want to remember"
              data-testid="event-notes-input"
            />
          </div>
        </div>

        <div className="p-5 flex items-center justify-end gap-2 sticky bottom-0" style={{ background: "var(--surface)" }}>
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary text-sm flex items-center gap-2"
            data-testid="event-save-button"
          >
            {busy ? <span className="spinner" /> : <Plus size={14} />}
            {busy ? "Saving..." : event ? "Save changes" : "Create event"}
          </button>
        </div>
      </form>
    </div>
  ), document.body);
}
